import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';

import Login from './pages/Login';
import Registrer from './pages/Registrer';
import GlemtPassord from './pages/GlemtPassord';
import TilbakestillPassord from './pages/TilbakestillPassord';
import Dashboard from './pages/Dashboard';
import Bilag from './pages/Bilag';
import Faktura from './pages/Faktura';
import FakturaNy from './pages/FakturaNy';
import FakturaVis from './pages/FakturaVis';
import Tilbud from './pages/Tilbud';
import TilbudNy from './pages/TilbudNy';
import TilbudVis from './pages/TilbudVis';
import Repeterende from './pages/Repeterende';
import Kontakter from './pages/Kontakter';
import Medlemmer from './pages/Medlemmer';
import Grupper from './pages/Grupper';
import Innsamling from './pages/Innsamling';
import Giverside from './pages/Giverside';
import GiversideTeam from './pages/GiversideTeam';
import Produkter from './pages/Produkter';
import Rapport from './pages/Rapport';
import Innstillinger from './pages/Innstillinger';
import Admin from './pages/Admin';
import Kontoplan from './pages/Kontoplan';
import Bokforing from './pages/Bokforing';
import Compliance from './pages/Compliance';
import Bank from './pages/Bank';
import Tjenester from './pages/Tjenester';
import Anleggsmidler from './pages/Anleggsmidler';
import Brukere from './pages/Brukere';
import Prosjekter from './pages/Prosjekter';
import Leverandorfaktura from './pages/Leverandorfaktura';
import Reiseregninger from './pages/Reiseregninger';
import MvaMelding from './pages/MvaMelding';
import Lonn from './pages/Lonn';
import SkattAga from './pages/SkattAga';
import Dokumenter from './pages/Dokumenter';
import Lager from './pages/Lager';
import Skann from './pages/Skann';
import InnboksSide from './pages/Innboks';
import Timer from './pages/Timer';
import Purring from './pages/Purring';
import Budsjett from './pages/Budsjett';
import Arrangement from './pages/Arrangement';
import Kommunikasjon from './pages/Kommunikasjon';
import AvtaleGiroOcr from './pages/AvtaleGiroOcr';
import Giverportal from './pages/Giverportal';
import ArrangementOffentlig from './pages/ArrangementOffentlig';
import Aksjeeierbok from './pages/Aksjeeierbok';
import Gjeld from './pages/Gjeld';
import Verdipapirer from './pages/Verdipapirer';
import ImportEksport from './pages/ImportEksport';

const Beskyttet = ({ children, tillatSimple = false }) => {
  const { user, loading, needsRegistration } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="fm-text-muted">Laster…</span>
      </div>
    );
  }
  if (needsRegistration) {
    // multi-tenant: håndter via egen "Opprett konto"-lenke i Login.js
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  // Simple-modus: kun /skann er tilgjengelig
  if (user.visning_modus === 'simple' && !tillatSimple) {
    return <Navigate to="/skann" replace />;
  }
  // /skann i full-modus: render uten Layout (bruker egen layout)
  if (tillatSimple && user.visning_modus === 'simple') {
    return children;
  }
  return <Layout>{children}</Layout>;
};

// When served from the admin subdomain (admin.regnino.cloud), send the root
// entry straight to the admin console so the subdomain acts as a dedicated
// admin front door. Auth pages and all other routes are untouched.
const isAdminHost = typeof window !== 'undefined' && /^admin\./i.test(window.location.hostname);

const AdminHostGate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (isAdminHost && location.pathname === '/') {
      navigate('/admin', { replace: true });
    }
  }, [location.pathname, navigate]);
  return null;
};

const App = () => (
  <>
    <AdminHostGate />
    <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/registrer" element={<Registrer />} />
    <Route path="/glemt-passord" element={<GlemtPassord />} />
    <Route path="/tilbakestill-passord" element={<TilbakestillPassord />} />
    <Route path="/gi/:slug" element={<Giverside />} />
    <Route path="/gi/:slug/team/:teamSlug" element={<GiversideTeam />} />
    <Route path="/giverportal" element={<Giverportal />} />
    <Route path="/a/:slug" element={<ArrangementOffentlig />} />
    <Route path="/" element={<Beskyttet><Dashboard /></Beskyttet>} />
    <Route path="/bilag" element={<Beskyttet><Bilag /></Beskyttet>} />
    <Route path="/faktura" element={<Beskyttet><Faktura /></Beskyttet>} />
    <Route path="/faktura/ny" element={<Beskyttet><FakturaNy /></Beskyttet>} />
    <Route path="/faktura/:id" element={<Beskyttet><FakturaVis /></Beskyttet>} />
    <Route path="/tilbud" element={<Beskyttet><Tilbud /></Beskyttet>} />
    <Route path="/tilbud/ny" element={<Beskyttet><TilbudNy /></Beskyttet>} />
    <Route path="/tilbud/:id" element={<Beskyttet><TilbudVis /></Beskyttet>} />
    <Route path="/repeterende" element={<Beskyttet><Repeterende /></Beskyttet>} />
    <Route path="/kontakter" element={<Beskyttet><Kontakter /></Beskyttet>} />
    <Route path="/medlemmer" element={<Beskyttet><Medlemmer /></Beskyttet>} />
    <Route path="/grupper" element={<Beskyttet><Grupper /></Beskyttet>} />
    <Route path="/innsamling" element={<Beskyttet><Innsamling /></Beskyttet>} />
    <Route path="/arrangement" element={<Beskyttet><Arrangement /></Beskyttet>} />
    <Route path="/kommunikasjon" element={<Beskyttet><Kommunikasjon /></Beskyttet>} />
    <Route path="/avtalegiro-ocr" element={<Beskyttet><AvtaleGiroOcr /></Beskyttet>} />
    <Route path="/aksjeeierbok" element={<Beskyttet><Aksjeeierbok /></Beskyttet>} />
    <Route path="/gjeld" element={<Beskyttet><Gjeld /></Beskyttet>} />
    <Route path="/verdipapirer" element={<Beskyttet><Verdipapirer /></Beskyttet>} />
    <Route path="/import-eksport" element={<Beskyttet><ImportEksport /></Beskyttet>} />
    <Route path="/produkter" element={<Beskyttet><Produkter /></Beskyttet>} />
    <Route path="/rapport" element={<Beskyttet><Rapport /></Beskyttet>} />
    <Route path="/kontoplan" element={<Beskyttet><Kontoplan /></Beskyttet>} />
    <Route path="/bokforing" element={<Beskyttet><Bokforing /></Beskyttet>} />
    <Route path="/bank" element={<Beskyttet><Bank /></Beskyttet>} />
    <Route path="/tjenester" element={<Beskyttet><Tjenester /></Beskyttet>} />
    <Route path="/anleggsmidler" element={<Beskyttet><Anleggsmidler /></Beskyttet>} />
    <Route path="/compliance" element={<Beskyttet><Compliance /></Beskyttet>} />
    <Route path="/brukere" element={<Beskyttet><Brukere /></Beskyttet>} />
    <Route path="/prosjekter" element={<Beskyttet><Prosjekter /></Beskyttet>} />
    <Route path="/innkjop" element={<Beskyttet><Leverandorfaktura /></Beskyttet>} />
    <Route path="/reise" element={<Beskyttet><Reiseregninger /></Beskyttet>} />
    <Route path="/mva" element={<Beskyttet><MvaMelding /></Beskyttet>} />
    <Route path="/lonn" element={<Beskyttet><Lonn /></Beskyttet>} />
    <Route path="/skatt-aga" element={<Beskyttet><SkattAga /></Beskyttet>} />
    <Route path="/dokumenter" element={<Beskyttet><Dokumenter /></Beskyttet>} />
    <Route path="/lager" element={<Beskyttet><Lager /></Beskyttet>} />
    <Route path="/innboks" element={<Beskyttet><InnboksSide /></Beskyttet>} />
    <Route path="/timer" element={<Beskyttet><Timer /></Beskyttet>} />
    <Route path="/purring" element={<Beskyttet><Purring /></Beskyttet>} />
    <Route path="/budsjett" element={<Beskyttet><Budsjett /></Beskyttet>} />
    <Route path="/skann" element={<Beskyttet tillatSimple><Skann /></Beskyttet>} />
    <Route path="/innstillinger" element={<Beskyttet><Innstillinger /></Beskyttet>} />
    <Route path="/admin" element={<Beskyttet><Admin /></Beskyttet>} />
    <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </>
);

export default App;
